/**
 * The session's capability answer, as served by the backend.
 *
 * WHAT CHANGED, AND WHY
 * ---------------------
 * This module used to hold STATIC_CAPABILITIES — a hand-copied duplicate of
 * backend/core/capabilities.py's map — and three pages held six more role
 * constants beside it. The copies were correct and carried comments naming
 * their source, which is the best a copy can do and still not enough:
 *
 *   - nothing could report a divergence, because one copy is Python and the
 *     other JSX, and no test can compare them;
 *   - the ROLE_ALIASES expansion that made them agree was invisible at every
 *     call site, so a mirror listing `owner` where the backend tuple did not
 *     looked like a bug and was not;
 *   - the two failure directions are not symmetric. Hiding a lawful control
 *     is silent — the user simply believes they cannot act. Offering an
 *     unlawful one produces a 403 after they have filled in the form.
 *
 * `GET /auth/me` now returns `capabilities`, computed by the same resolver
 * that enforces them (core/capabilities.capabilities_for). There is one
 * answer, and this file reads it.
 *
 * WHAT THIS IS NOT
 * ----------------
 * A capability is not an entitlement, a feature flag, or a gate verdict:
 *
 *   capability   may THIS USER perform the action           403 capability_required
 *   entitlement  is THIS TENANT subscribed to the surface   403 tenant_not_entitled
 *   feature flag is the surface switched on in this deploy  503 not_enabled
 *   gate         does the WORK permit it right now          409 / refusal payload
 *
 * They refuse for different reasons and a user can hold one while lacking
 * another. Collapsing them would make "you may not" indistinguishable from
 * "not here" and from "not yet". Keep them separate at the call site.
 *
 * THE BACKEND REMAINS AUTHORITATIVE. This decides whether to OFFER a control.
 * A hidden button is not a security boundary and a visible one is not
 * authorization; the 403 is the only real denial.
 */

/**
 * True when the session was granted `capability`.
 *
 * FAILS CLOSED on anything that is not a list of strings — absent, null, a
 * pending fetch, an object, a malformed payload. An unreadable answer is not
 * an open door, and it is not a reason to fall back to guessing from a role.
 */
export const hasCapability = (capabilities, capability) => {
  if (!Array.isArray(capabilities)) return false;
  if (typeof capability !== "string" || !capability) return false;
  return capabilities.includes(capability);
};

/**
 * True only when every named capability is held. For controls that sit behind
 * an intersection at the route — see `rfq.authorize`, which the backend
 * already projects as its own intersected capability, so prefer asking for
 * that one rather than re-deriving it here.
 */
export const hasEveryCapability = (capabilities, ...required) =>
  required.length > 0 && required.every((c) => hasCapability(capabilities, c));

export const CAPABILITY_ACCESS = Object.freeze({
  NOT_ESTABLISHED: "NOT_ESTABLISHED",
  ALLOWED: "ALLOWED",
  DENIED: "DENIED",
});

/**
 * Presentation state for a surface opened by any one of several capabilities.
 * A pending session answer fails closed for controls but is not a denial.
 */
export const anyCapabilityAccess = (
  capabilitiesEstablished,
  capabilities,
  ...required
) => {
  if (!capabilitiesEstablished) return CAPABILITY_ACCESS.NOT_ESTABLISHED;
  return required.some((capability) => hasCapability(capabilities, capability))
    ? CAPABILITY_ACCESS.ALLOWED
    : CAPABILITY_ACCESS.DENIED;
};
